import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const API_URL = process.env.REACT_APP_API_URL;

export const fetchDashboardPersonalTasks = createAsyncThunk(
  'dashboardPersonal/fetchTasks',
  async ({ userEmail }, { signal, rejectWithValue }) => {
    try {
      const email = userEmail || '';
      const res = await fetch(
        `${API_URL}/api/dashboard/personal/tasks?email=${encodeURIComponent(email)}`,
        { signal }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch personal tasks');
      if (!json?.success) throw new Error(json?.message || 'Failed to fetch personal tasks');
      return json?.data || {};
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      console.error('dashboardPersonal/fetchTasks failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch personal tasks');
    }
  }
);

export const fetchDashboardCalendarTwoWeeks = createAsyncThunk(
  'dashboardPersonal/fetchCalendarTwoWeeks',
  async ({ userEmail }, { signal, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ userEmail: userEmail || '' }).toString();
      const res = await fetch(`${API_URL}/api/CalendarDataTwoWeeks?${params}`, { signal });
      if (!res.ok) throw new Error(`Failed to fetch two-week calendar: HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      console.error('dashboardPersonal/fetchCalendarTwoWeeks failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch two-week calendar');
    }
  }
);

export const updateDashboardPersonalTask = createAsyncThunk(
  'dashboardPersonal/updateTask',
  async ({ taskId, startDate, dueDate, completed }, { signal, rejectWithValue }) => {
    try {
      const body = { taskId };
      if (startDate !== undefined) body.startDate = startDate;
      if (dueDate !== undefined) body.dueDate = dueDate;
      if (completed !== undefined) body.completed = completed;

      const res = await fetch(`${API_URL}/api/dashboard/personal/task/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || 'Failed to update task');
      return json;
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
      console.error('dashboardPersonal/updateTask failed', e);
      return rejectWithValue(e?.message || 'Failed to update task');
    }
  }
);

const initialState = {
  tasks: {
    overdue: [],
    thisWeek: [],
    nextWeek: [],
  },
  tasksLoading: false,
  tasksError: null,

  twoWeekData: null,
  twoWeekLoading: false,
  twoWeekError: null,
};

const dashboardPersonalSlice = createSlice({
  name: 'dashboardPersonal',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardPersonalTasks.pending, (state) => {
        state.tasksLoading = true;
        state.tasksError = null;
      })
      .addCase(fetchDashboardPersonalTasks.fulfilled, (state, action) => {
        state.tasksLoading = false;
        state.tasks = action.payload || { overdue: [], thisWeek: [], nextWeek: [] };
      })
      .addCase(fetchDashboardPersonalTasks.rejected, (state, action) => {
        state.tasksLoading = false;
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.tasksError = action.payload || action.error?.message || 'Failed to fetch personal tasks';
      })

      .addCase(fetchDashboardCalendarTwoWeeks.pending, (state) => {
        state.twoWeekLoading = true;
        state.twoWeekError = null;
      })
      .addCase(fetchDashboardCalendarTwoWeeks.fulfilled, (state, action) => {
        state.twoWeekLoading = false;
        state.twoWeekData = action.payload ?? null;
      })
      .addCase(fetchDashboardCalendarTwoWeeks.rejected, (state, action) => {
        state.twoWeekLoading = false;
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.twoWeekError = action.payload || action.error?.message || 'Failed to fetch two-week calendar';
      })
      .addCase(updateDashboardPersonalTask.rejected, (state, action) => {
        // Do not treat aborted updates as errors
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.tasksError = action.payload || action.error?.message || state.tasksError;
      });
  },
});

export default dashboardPersonalSlice.reducer;


